<script setup lang="ts">
import { computed } from 'vue';
import { COMMENT_SHARE } from '@/constants';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import { AdpDocument } from '@/types';
import { getArticlePageUrl } from '@/utils';
import { useIsNextLayout } from '@/utils/composables/useLayoutVariant';
import AdpDocumentTooltipUserListNext from './adp-document-tooltip-user-list-next.vue';
import AdpDocumentTooltipUserList from './adp-document-tooltip-user-list.vue';

const props = withDefaults(
  defineProps<{
    adpDocument: AdpDocument;
    commentedUsers: {
      id: number;
      name: string;
      commentId: number;
    }[];
    disabled?: boolean;
    size?: 's' | 'm';
  }>(),
  {
    disabled: false,
    size: 'm',
  },
);

const isNextLayout = useIsNextLayout();

const commentCount = computed(() => props.commentedUsers.length);

const tooltipUsers = computed(() =>
  props.commentedUsers.map(user => ({
    id: user.id,
    name: user.name,
    to: isNextLayout
      ? getArticlePageUrl(props.adpDocument, user.commentId)
      : { name: 'groupComment', params: { commentId: user.commentId } },
  })),
);
</script>

<template>
  <VTooltip theme="menu" placement="top">
    <button
      class="comment-button"
      :class="{ disabled, small: size === 's' }"
      :disabled="disabled"
    >
      <DgrIcon
        class="comment-icon"
        size="small"
        name="comment"
        :keep-fill="false"
        alt="comment icon"
      />
      <div class="comment-total-count c-title-bold--s" v-if="commentCount > 0">
        {{ commentCount }}
      </div>
    </button>

    <template #popper>
      <AdpDocumentTooltipUserListNext
        v-if="isNextLayout"
        action="comment"
        :users="tooltipUsers"
      />
      <AdpDocumentTooltipUserList
        v-else
        :users="tooltipUsers"
        :empty-text="COMMENT_SHARE"
      />
    </template>
  </VTooltip>
</template>

<style lang="scss" scoped>
.comment-button {
  all: unset;
  display: flex;
  align-items: center;
  padding: 8px 10px;
  width: fit-content;
  min-width: 32px;
  border-radius: 4px;
  cursor: pointer;

  &:not(.disabled):hover {
    background-color: $color-gray200;
  }

  &.disabled {
    color: $color-gray600;
    border: none;
    cursor: not-allowed;
  }

  .comment-icon {
    margin-right: 4px;
  }

  &.small {
    padding: 4px;
  }
}
</style>
